/**
 * Service for handling file downloads and creating download links
 */

import JSZip from 'jszip';

import type { IFileAttachment } from '../types/chat';
import { apiService } from './apiService';
import type { AppealLetterFormat } from './apiService';
import { API_CONFIG } from './apiConfig';

/** Same pattern as ChatHelper.extractFileIdFromUrl (kept here to avoid services↔helpers cycles). */
function extractFileIdFromAttachmentUrl(url: string): string | null {
  if (!url) return null;

  const match = url.match(/\/files\/([a-f0-9-]+)-/);
  return match ? match[1] : null;
}

function resolveAttachmentFileId(file: IFileAttachment): string | null {
  let fileId = (file.id as string | null) ?? null;

  if (!fileId) {
    const fileUrl = file.file_url || file.url;
    fileId = extractFileIdFromAttachmentUrl(fileUrl as string);
  }

  return fileId;
}

/** Extension from the actual filename, not the (possibly extension-less) display name. */
function attachmentExtension(file: IFileAttachment): string {
  const source = file.original_name || file.file_name || file.name || '';
  const dot = source.lastIndexOf('.');

  return dot > 0 ? source.slice(dot) : '';
}

function attachmentDisplayName(file: IFileAttachment, index: number): string {
  const fallback =
    file.original_name || file.file_name || file.name || `file-${index + 1}`;

  if (!file.display_name) return fallback;

  const ext = attachmentExtension(file);
  const hasExt = !ext || file.display_name.toLowerCase().endsWith(ext.toLowerCase());

  return hasExt ? file.display_name : `${file.display_name}${ext}`;
}

/** Strip path segments and traversal from zip entry names. */
function sanitizeZipEntryName(name: string, fallback: string): string {
  const base = name.replace(/^.*[/\\]/, '').replace(/\.\./g, '_').trim();
  
  return base || fallback;
}

/** Hand a blob to the browser as a download. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);

  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

/** Force-download a blob via a temporary `<a download>` link, returning the object URL used. */
function triggerBlobDownload(blob: Blob, filename: string): FileDownloadResult {
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(url), 1000);

  return { success: true, url };
}

export interface AppealLetterDownloadResult {
  success: boolean;
  error?: string;
}

export interface AppealLetterFetchResult extends AppealLetterDownloadResult {
  blob?: Blob;
  /** The name the server chose, already falling back if it sent none. */
  filename?: string;
}

/**
 * Fetch a chat's appeal letter as rendered by the backend (REV-2047): the
 * latest, or `version`. It is not saved, for callers that put it somewhere
 * else, such as the attachments zip.
 *
 * `fallbackBaseName` is used only when the response carries no filename of its
 * own; normally the server names the file after the claim.
 */
export async function fetchAppealLetter(
  chatId: string,
  format: AppealLetterFormat,
  fallbackBaseName?: string | null,
  version?: number,
): Promise<AppealLetterFetchResult> {
  const response = await apiService.downloadAppealLetter(chatId, format, version);

  if (!response.success || !response.data) {
    return {
      success: false,
      error: response.error || 'Failed to download the appeal letter',
    };
  }

  return {
    success: true,
    blob: response.data.blob,
    filename:
      response.data.filename ||
      `appeal_letter_${fallbackBaseName || chatId}.${format}`,
  };
}

/** Fetch the appeal letter and hand it straight to the browser. */
export async function saveAppealLetter(
  chatId: string,
  format: AppealLetterFormat,
  fallbackBaseName?: string | null,
  version?: number,
): Promise<AppealLetterDownloadResult> {
  const result = await fetchAppealLetter(chatId, format, fallbackBaseName, version);

  if (!result.success || !result.blob) {
    return { success: false, error: result.error };
  }

  saveBlob(result.blob, result.filename!);

  return { success: true };
}

export interface FileDownloadOptions {
  inline?: boolean;
  filename?: string;
}

export interface FileDownloadResult {
  success: boolean;
  url?: string;
  blob?: Blob;
  error?: string;
}

/**
 * Downloads a file and creates a download link
 * @param fileId - The ID of the file to download
 * @param options - Download options including inline behavior and filename
 * @returns Promise with download result
 */
export async function downloadFile(
  fileId: string, 
  options: FileDownloadOptions = {}
): Promise<FileDownloadResult> {
  const { inline = false, filename } = options;
  
  try {
    const response = await apiService.downloadFile(fileId, inline);
    
    if (!response.success || !response.data) {
      return {
        success: false,
        error: response.error || 'Failed to download file'
      };
    }

    // Create object URL for the blob
    const url = URL.createObjectURL(response.data);
    
    // If inline is false, trigger download
    if (!inline) {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `file-${fileId}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the object URL after a short delay
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      
      return {
        success: true,
        url: url
      };
    }
    
    // For inline viewing, return the URL and blob
    return {
      success: true,
      url: url,
      blob: response.data
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

/**
 * Creates a download link for a file without actually downloading it
 * @param fileId - The ID of the file
 * @param filename - Optional filename for the download
 * @returns The download URL
 */
export function createDownloadLink(fileId: string, filename?: string): string {
  const params = new URLSearchParams();
  params.append('inline', 'false');
  params.append('download', 'true');
  if (filename) {
    params.append('filename', filename);
  }
  
  return `${API_CONFIG.BASE_URL}/files/${fileId}/download/?${params.toString()}`;
}

/**
 * Creates a view link for a file (inline viewing)
 * @param fileId - The ID of the file
 * @returns The view URL
 */
export function createViewLink(fileId: string): string {
  return `${API_CONFIG.BASE_URL}/files/${fileId}/download/`;
}

/**
 * Downloads a file as a blob for processing
 * @param fileId - The ID of the file to download
 * @returns Promise with the blob data
 */
export async function downloadFileAsBlob(fileId: string): Promise<FileDownloadResult> {
  return downloadFile(fileId, { inline: true });
}

/**
 * Forces a file download using the HTML5 download attribute
 * This method is more reliable for forcing downloads in all browsers
 * @param fileId - The ID of the file to download
 * @param filename - Optional filename for the download
 * @returns Promise with download result
 */
export async function forceDownloadFile(fileId: string, filename?: string): Promise<FileDownloadResult> {
  try {
    const response = await apiService.downloadFile(fileId, false);

    if (!response.success || !response.data) {
      return {
        success: false,
        error: response.error || 'Failed to download file'
      };
    }

    return triggerBlobDownload(response.data, filename || `file-${fileId}`);
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

/**
 * Same as {@link forceDownloadFile}, but for document ids (policy/contract citations),
 * which live in a different backend resource than uploaded files (see
 * `apiService.downloadDocumentById`).
 */
export async function forceDownloadDocumentById(documentId: string, filename?: string): Promise<FileDownloadResult> {
  try {
    const response = await apiService.downloadDocumentById(documentId);

    if (!response.success || !response.data) {
      return {
        success: false,
        error: response.error || 'Failed to download document'
      };
    }

    return triggerBlobDownload(response.data, filename || `document-${documentId}`);
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

export interface DownloadAttachmentsZipResult {
  success: boolean;
  error?: string;
}

/**
 * Fetches each attachment as a blob and delivers one .zip download (multiple files only).
 * Files without a resolvable API id attempt fetch from file_url (may fail cross-origin).
 */
export async function downloadAttachmentsAsZip(
  files: IFileAttachment[],
  zipFilename = 'attachments.zip',
  extraEntries?: Array<{ name: string; blob: Blob }>,
): Promise<DownloadAttachmentsZipResult> {
  const hasExtras = extraEntries && extraEntries.length > 0;
  if (!files.length && !hasExtras) {
    return { success: false, error: 'No files to download' };
  }

  const zip = new JSZip();
  const usedNames = new Set<string>();
  let added = 0;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const fallbackName = `file-${i + 1}`;
    let baseName = sanitizeZipEntryName(
      attachmentDisplayName(file, i),
      fallbackName,
    );

    let entryName = baseName;
    let suffix = 2;
    while (usedNames.has(entryName)) {
      const dot = baseName.lastIndexOf('.');
      entryName =
        dot > 0
          ? `${baseName.slice(0, dot)} (${suffix})${baseName.slice(dot)}`
          : `${baseName} (${suffix})`;
      suffix += 1;
    }
    usedNames.add(entryName);

    const fileId = resolveAttachmentFileId(file);
    let blob: Blob | null = null;

    if (fileId) {
      const response = await apiService.downloadFile(fileId, true);
      if (response.success && response.data) {
        blob = response.data;
      }
    } else {
      const fileUrl = file.file_url || file.url;
      if (fileUrl) {
        try {
          const res = await fetch(fileUrl, { credentials: 'include', mode: 'cors' });
          if (res.ok) {
            blob = await res.blob();
          }
        } catch {
          blob = null;
        }
      }
    }

    if (blob) {
      zip.file(`input/${entryName}`, blob);
      added += 1;
    }
  }

  if (hasExtras) {
    for (const entry of extraEntries!) {
      zip.file(`output/${entry.name}`, entry.blob);
      added += 1;
    }
  }

  if (added === 0) {
    return { success: false, error: 'No files could be downloaded' };
  }

  const content = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(content);
  const name = zipFilename.toLowerCase().endsWith('.zip')
    ? zipFilename
    : `${zipFilename}.zip`;

  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  return { success: true };
}
